<template>
  <div class="stacked-document-display" :class="[aspectRatioClass, { 'is-fullscreen': isFullscreen }]">
    <div v-if="documents.length > 0" class="documents-stack">
      <!-- Loop through all documents and display them -->
      <div
        v-for="(document, index) in documents"
        :key="index"
        class="document-item"
      >
        <div class="document-container">
          <!-- Document Preview -->
          <div class="document-preview">
            <!-- Show video if it's an MP4/MOV file -->
            <div class="media-container video-container" v-if="isVideoFile(document.type) && (document.preview || document.dataUrl)">
              <div class="media-content">
                <video
                  autoplay
                  loop
                  muted
                  class="document-video"
                  :src="document.preview || document.dataUrl"
                  :alt="document.name"
                  @error="handleMediaError"
                ></video>
              </div>

              <!-- Add Live Transcript component -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- If it's a video but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isVideoFile(document.type)" class="media-container video-placeholder">
              <div class="media-content">
                <div class="placeholder-icon">🎥</div>
                <div class="video-name">{{ document.name }}</div>
                <div class="video-info">Video preview not available</div>
              </div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- Show image with animation if it's an image and we have preview data -->
            <div class="media-container image-container" v-else-if="isImageFile(document.type) && (document.preview || document.dataUrl)">
              <div class="media-content">
                <div class="image-animation-container" :class="currentAnimation">
                  <img
                    :src="document.preview || document.dataUrl"
                    :alt="document.name"
                    class="document-image"
                    @error="handleMediaError"
                  />
                </div>
              </div>

              <!-- Add Live Transcript component -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- If it's an image but we DON'T have preview data, show name-based placeholder -->
            <div v-else-if="isImageFile(document.type)" class="media-container image-placeholder">
              <div class="media-content">
                <div class="placeholder-icon">🖼️</div>
                <div class="image-name">{{ document.name }}</div>
                <div class="image-info">Image preview not available</div>
              </div>

              <!-- Add Live Transcript component for placeholder too -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>

            <!-- Non-image, non-video document (PDF, DOCX, etc) -->
            <div v-else class="media-container file-icon-container">
              <div class="media-content">
                <div class="file-icon" :class="getFileIconClass(document.type)">
                  {{ getFileExtension(document.name).toUpperCase() }}
                </div>
                <div class="document-name">{{ document.name }}</div>
              </div>

              <!-- Add Live Transcript component for document files -->
              <live-transcript
                v-show="isPlaying"
                :voice-speed="voiceSpeed"
                :text="activeSegmentContent"
                :speaker="activeSegmentSpeaker"
                :speaker-image="speakerImage"
                :visible="isPlaying"
                :message-id="messageId"
                :background-color="transcriptBackgroundColor"
              />
            </div>
          </div>
        </div>
      </div>
    </div>

    <!-- No documents placeholder -->
    <div v-else class="media-container no-documents">
      <div class="media-content">
        <div class="placeholder-icon">📄</div>
        <p>No documents available</p>
      </div>

      <!-- Add Live Transcript component for empty state -->
      <live-transcript
        v-show="isPlaying"
        :voice-speed="voiceSpeed"
        :text="activeSegmentContent"
        :speaker="activeSegmentSpeaker"
        :speaker-image="speakerImage"
        :visible="isPlaying"
        :message-id="messageId"
        :background-color="transcriptBackgroundColor"
      />
    </div>

    <!-- Large play button (only visible when paused) -->
    <div v-if="!isPlaying" class="large-play-button-container" @click="togglePlay">
      <button class="large-play-button" aria-label="Play">
        <svg xmlns="http://www.w3.org/2000/svg" width="48" height="48" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <polygon points="5 3 19 12 5 21 5 3"></polygon>
        </svg>
      </button>
    </div>

    <!-- Aspect ratio selector (only visible when not playing) -->
    <div v-if="!isPlaying" class="aspect-ratio-selector">
      <button
        v-for="ratio in availableAspectRatios"
        :key="ratio.value"
        @click="setAspectRatio(ratio.value)"
        :class="{ active: aspectRatio === ratio.value }"
        class="aspect-button"
      >
        {{ ratio.label }}
      </button>
    </div>

    <!-- Animation style selector (only visible when not playing and only for images) -->
    <div v-if="!isPlaying && hasImages" class="animation-style-selector">
      <label>Animation Style:</label>
      <div class="animation-buttons">
        <button
          v-for="anim in availableAnimations"
          :key="anim.value"
          @click="setAnimationStyle(anim.value)"
          :class="{ active: animationStyle === anim.value }"
          class="animation-button"
        >
          {{ anim.label }}
        </button>
      </div>
    </div>

    <!-- Fullscreen toggle button (always visible) -->
    <button
      @click="toggleFullscreen"
      class="fullscreen-toggle"
      :class="{ 'is-fullscreen': isFullscreen }"
      aria-label="Toggle fullscreen"
    >
      <svg v-if="!isFullscreen" xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M8 3H5a2 2 0 0 0-2 2v3"></path>
        <path d="M21 8V5a2 2 0 0 0-2-2h-3"></path>
        <path d="M3 16v3a2 2 0 0 0 2 2h3"></path>
        <path d="M16 21h3a2 2 0 0 0 2-2v-3"></path>
      </svg>
      <svg v-else xmlns="http://www.w3.org/2000/svg" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
        <path d="M8 3v3a2 2 0 0 1-2 2H3"></path>
        <path d="M21 8h-3a2 2 0 0 1-2-2V3"></path>
        <path d="M3 16h3a2 2 0 0 1 2 2v3"></path>
        <path d="M16 21v-3a2 2 0 0 1 2-2h3"></path>
      </svg>
    </button>
  </div>
</template>

<script>
import LiveTranscript from '../components/LiveTranscript.vue';

export default {
  name: 'StackedDocumentDisplay',
  components: {
    LiveTranscript
  },
  props: {
    messageId: {
      required: true,
    },
    voiceSpeed: {
      type: Number,
      default: 1
    },
    speakerImage: {
      type: String,
      default: '/static/Gemini_Generated_Image_uobiifuobiifuobi.jpeg'
    },
    documents: {
      type: Array,
      default: () => []
    },
    isPlaying: {
      type: Boolean,
      default: false
    },
    activeSegmentContent: {
      type: String,
      default: ''
    },
    activeSegmentSpeaker: {
      type: String,
      default: ''
    },
    transcriptBackgroundColor: {
      type: String,
      default: 'rgba(0, 0, 0, 0.6)' // Default black with 0.7 opacity
    }
  },
  data() {
    return {
      // All available animations
      availableAnimations: [
        { value: 'random', label: 'Random' },
        { value: 'zoom-pan', label: 'Zoom & Pan' },
        { value: 'ken-burns', label: 'Ken Burns' },
        { value: 'fade-scale', label: 'Fade & Scale' },
        { value: 'gentle-float', label: 'Gentle Float' },
        { value: 'smooth-slide', label: 'Smooth Slide' }
      ],
      // All available aspect ratios
      availableAspectRatios: [
        { value: '16:9', label: '16:9' },
        { value: '4:3', label: '4:3' },
        { value: '1:1', label: '1:1' },
        { value: '9:16', label: '9:16' }
      ],
      animations: [
        'zoom-pan-effect',
        'ken-burns-effect',
        'fade-and-scale',
        'gentle-float',
        'smooth-slide'
      ],
      showOverlayEffects: true,
      animationIndex: 0,
      animationStyle: 'random', // Default to random cycling
      aspectRatio: '16:9', // Default to standard 16:9
      isFullscreen: false,
      animationTimer: null,
      currentAnimation: 'zoom-pan-effect' // Initial animation
    };
  },
  computed: {
    // Determine if there are images to animate
    hasImages() {
      return this.documents.some(doc => this.isImageFile(doc.type));
    },
    // Get current aspect ratio class for CSS
    aspectRatioClass() {
      switch (this.aspectRatio) {
        case '16:9': return 'aspect-16-9';
        case '4:3': return 'aspect-4-3';
        case '1:1': return 'aspect-1-1';
        case '9:16': return 'aspect-9-16';
        default: return 'aspect-16-9';
      }
    }
  },
  watch: {
    // Watch for transcript background color changes
    transcriptBackgroundColor: {
      immediate: true,
      handler(newColor) {
        // Force a re-render if needed
        this.$forceUpdate();
      }
    }
  },
  mounted() {
    // Load saved settings
    this.loadSettings();

    // Start animation cycle based on selected style
    this.startAnimationCycle();

    // Add fullscreen change event listener
    document.addEventListener('fullscreenchange', this.handleFullscreenChange);
    document.addEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  },
  beforeDestroy() {
    // Clean up timers and event listeners
    if (this.animationTimer) {
      clearInterval(this.animationTimer);
    }

    document.removeEventListener('fullscreenchange', this.handleFullscreenChange);
    document.removeEventListener('webkitfullscreenchange', this.handleFullscreenChange);
  },
  methods: {
    // Toggle play/pause
    togglePlay() {
      this.$emit('toggle-play');
    },
    isImageFile(fileType) {
      return fileType && fileType.startsWith('image/');
    },

    isVideoFile(fileType) {
      return fileType && (
        fileType.startsWith('video/') ||
        fileType.includes('mp4') ||
        fileType.includes('mov') ||
        fileType.includes('quicktime') ||
        fileType.includes('webm') ||
        fileType.includes('ogg')
      );
    },

    getFileExtension(filename) {
      if (!filename) return '';
      return filename.split('.').pop() || '';
    },

    getFileIconClass(fileType) {
      if (!fileType) return 'generic-icon';
      if (fileType.includes('pdf')) return 'pdf-icon';
      if (fileType.includes('word') || fileType.includes('doc')) return 'doc-icon';
      if (fileType.includes('excel') || fileType.includes('sheet') || fileType.includes('xls')) return 'xls-icon';
      if (fileType.includes('powerpoint') || fileType.includes('presentation') || fileType.includes('ppt')) return 'ppt-icon';
      if (this.isVideoFile(fileType)) return 'video-icon';
      return 'generic-icon';
    },

    handleMediaError(e) {
      console.warn('Media load error:', e.target.alt);
      e.target.style.display = 'none';

      // Create a placeholder
      const placeholder = document.createElement('div');
      placeholder.className = 'file-icon generic-icon';
      placeholder.textContent = this.getFileExtension(e.target.alt).toUpperCase();
      e.target.parentNode.appendChild(placeholder);

      // Add a message about the failed load
      const errorMsg = document.createElement('div');
      errorMsg.className = 'media-error-message';
      errorMsg.textContent = `${e.target.tagName === 'VIDEO' ? 'Video' : 'Image'} could not be loaded`;
      e.target.parentNode.appendChild(errorMsg);
    },

    // Start the animation cycle based on selected style
    startAnimationCycle() {
      // Clear any existing timer
      if (this.animationTimer) {
        clearInterval(this.animationTimer);
      }

      // If style is 'random', cycle through animations
      if (this.animationStyle === 'random') {
        this.animationTimer = setInterval(() => {
          this.animationIndex = (this.animationIndex + 1) % this.animations.length;
          this.currentAnimation = this.animations[this.animationIndex];
        }, 8000); // Change animation every 8 seconds
      } else {
        // Set to specific animation
        const animMap = {
          'zoom-pan': 'zoom-pan-effect',
          'ken-burns': 'ken-burns-effect',
          'fade-scale': 'fade-and-scale',
          'gentle-float': 'gentle-float',
          'smooth-slide': 'smooth-slide'
        };
        this.currentAnimation = animMap[this.animationStyle] || 'zoom-pan-effect';
      }
    },

    // Set animation style
    setAnimationStyle(style) {
      this.animationStyle = style;
      this.startAnimationCycle();
      this.saveSettings();
    },

    // Set aspect ratio
    setAspectRatio(ratio) {
      this.aspectRatio = ratio;
      this.saveSettings();
    },

    // Toggle fullscreen
    toggleFullscreen() {
      if (!this.isFullscreen) {
        this.enterFullscreen();
      } else {
        this.exitFullscreen();
      }
    },
    // Replace your existing enterFullscreen method with this improved version
    enterFullscreen() {
      const element = this.$el;

      try {
        // Try standard fullscreen API
        if (element.requestFullscreen) {
          element.requestFullscreen();
        } else if (element.webkitRequestFullscreen) {
          element.webkitRequestFullscreen();
        } else if (element.msRequestFullscreen) {
          element.msRequestFullscreen();
        }

        // Apply fullscreen class regardless of API success
        // This helps on iOS where the API might not work
        this.isFullscreen = true;

        // Add class to body for additional styling control
        document.body.classList.add('has-fullscreen-element');

        // Lock orientation to landscape if possible
        if (screen.orientation && screen.orientation.lock) {
          screen.orientation.lock('landscape').catch(e => {
            console.log('Orientation lock not supported');
          });
        }

        // Add event listeners for resizing and orientation changes
        window.addEventListener('resize', this.handleFullscreenResize);
        window.addEventListener('orientationchange', this.handleFullscreenResize);

        // Force immediate resize handling
        this.handleFullscreenResize();
      } catch (e) {
        console.error('Fullscreen API error:', e);
        // Fallback for devices without proper fullscreen support
        this.isFullscreen = true;
        document.body.classList.add('has-fullscreen-element');
      }
    },

    // Replace your existing exitFullscreen method
    exitFullscreen() {
      try {
        if (document.exitFullscreen) {
          document.exitFullscreen();
        } else if (document.webkitExitFullscreen) {
          document.webkitExitFullscreen();
        } else if (document.msExitFullscreen) {
          document.msExitFullscreen();
        }

        // Unlock orientation if it was locked
        if (screen.orientation && screen.orientation.unlock) {
          screen.orientation.unlock();
        }

        // Remove event listeners
        window.removeEventListener('resize', this.handleFullscreenResize);
        window.removeEventListener('orientationchange', this.handleFullscreenResize);

        // Remove body class
        document.body.classList.remove('has-fullscreen-element');
      } catch (e) {
        console.error('Exit fullscreen error:', e);
      }

      // Always reset the fullscreen state
      this.isFullscreen = false;
    },

    // Improved fullscreen resize handler
    handleFullscreenResize() {
      if (this.isFullscreen) {
        // Force redraw of the fullscreen container
        this.$nextTick(() => {
          const element = this.$el;
          if (element) {
            // For mobile browsers, especially iOS, we need to force a reflow
            // by temporarily modifying a style property
            element.style.display = 'none';
            // This line accesses offsetHeight which causes a recalculation
            const forceRepaint = element.offsetHeight;
            element.style.display = '';

            // Apply a slight delay before forcing another reflow for iOS
            setTimeout(() => {
              // Force hardware acceleration
              element.style.transform = 'translateZ(0)';

              // For some mobile browsers, we need to ensure content fills the screen
              const viewportHeight = window.innerHeight;
              if (element.offsetHeight < viewportHeight) {
                element.style.height = `${viewportHeight}px`;
              }
            }, 50);
          }
        });
      }
    },

    // Update handleFullscreenChange method
    handleFullscreenChange() {
      const isInFullscreen = !!(document.fullscreenElement ||
                            document.webkitFullscreenElement ||
                            document.msFullscreenElement);

      this.isFullscreen = isInFullscreen;

      // Toggle body class based on fullscreen state
      if (isInFullscreen) {
        document.body.classList.add('has-fullscreen-element');
      } else {
        document.body.classList.remove('has-fullscreen-element');
      }
    },

    // Save settings to localStorage
    saveSettings() {
      localStorage.setItem(`display_settings_${this.messageId}`, JSON.stringify({
        aspectRatio: this.aspectRatio,
        animationStyle: this.animationStyle
      }));
    },

    // Load settings from localStorage
    loadSettings() {
      const savedSettings = localStorage.getItem(`display_settings_${this.messageId}`);
      if (savedSettings) {
        try {
          const settings = JSON.parse(savedSettings);
          if (settings.aspectRatio) {
            this.aspectRatio = settings.aspectRatio;
          }
          if (settings.animationStyle) {
            this.animationStyle = settings.animationStyle;
          }
        } catch (e) {
          console.error('Error loading saved settings:', e);
        }
      }
    }
  }
};
</script>

<style scoped>
/* Main container with aspect ratio control */
.stacked-document-display {
  width: 100%;
  position: relative;
  background-color: rgba(0, 0, 0, 0.2);
  border-radius: 12px;
  overflow: hidden;
}

/* Aspect ratio classes */
.aspect-16-9 {
  padding-top: 56.25%; /* 16:9 Aspect Ratio */
}

.aspect-4-3 {
  padding-top: 75%; /* 4:3 Aspect Ratio */
}

.aspect-1-1 {
  padding-top: 100%; /* 1:1 Square Aspect Ratio */
}

.aspect-9-16 {
  padding-top: 177.78%; /* 9:16 Vertical Aspect Ratio */
}

/* Fullscreen mode overrides */
.stacked-document-display.is-fullscreen {
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  width: 100vw;
  height: 100vh;
  padding-top: 0 !important;
  border-radius: 0;
  z-index: 9999;
  background-color: black;
}

.documents-counter {
  text-align: center;
  padding: 0.5rem;
  margin-bottom: 1rem;
  font-size: 0.875rem;
  color: rgba(255, 255, 255, 0.9);
  background-color: rgba(4, 255, 134, 0.1);
  border-radius: 999px;
  display: inline-block;
}

.documents-stack {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

.document-item {
  position: relative;
  width: 100%;
  height: 100%;
}

.document-container {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  width: 100%;
  height: 100%;
  overflow: hidden;
  position: relative;
}

.document-preview {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Media container for consistent sizing */
.media-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.media-content {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
}

/* Image container with animations */
.image-container {
  position: relative;
  width: 100%;
  height: 100%;
  overflow: hidden;
}

.image-animation-container {
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
}

.document-image {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* Enhanced animation keyframes */
.zoom-pan-effect {
  animation: zoomPan 15s ease-in-out infinite;
}

.ken-burns-effect {
  animation: kenBurns 20s ease-in-out infinite;
}

.fade-and-scale {
  animation: fadeScale 12s ease-in-out infinite;
}

.gentle-float {
  animation: gentleFloat 10s ease-in-out infinite;
}

.smooth-slide {
  animation: smoothSlide 18s ease-in-out infinite;
}

/* Updated animation keyframes with smoother loops */
@keyframes zoomPan {
  0%, 100% {
    transform: scale(1) translate(0, 0);
  }
  50% {
    transform: scale(1.1) translate(-2%, -1%);
  }
}

@keyframes kenBurns {
  0%, 100% {
    transform: scale(1.05) translate(-1%, -1%);
  }
  25% {
    transform: scale(1.09) translate(0%, 1%);
  }
  50% {
    transform: scale(1.12) translate(1%, 1%);
  }
  75% {
    transform: scale(1.09) translate(0%, 0%);
  }
}

@keyframes fadeScale {
  0%, 100% {
    opacity: 0.95;
    transform: scale(1);
  }
  50% {
    opacity: 1;
    transform: scale(1.08);
  }
}

@keyframes gentleFloat {
  0%, 100% {
    transform: translateY(0);
  }
  50% {
    transform: translateY(-10px);
  }
}

@keyframes smoothSlide {
  0%, 100% {
    transform: translateX(-2%) scale(1.05);
  }
  50% {
    transform: translateX(2%) scale(1.05);
  }
}

/* Improved overlay effects */
.image-overlay {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

.overlay-element {
  position: absolute;
  border-radius: 50%;
  background: rgba(255, 255, 255, 0.08);
  filter: blur(8px);
}

.element-1 {
  width: 40%;
  height: 40%;
  top: -10%;
  left: -10%;
  animation: drift1 25s infinite alternate;
}

.element-2 {
  width: 25%;
  height: 25%;
  bottom: 10%;
  right: 5%;
  animation: drift2 20s infinite alternate;
}

.element-3 {
  width: 35%;
  height: 35%;
  bottom: -15%;
  left: 30%;
  animation: drift3 30s infinite alternate;
}

@keyframes drift1 {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(10%, 10%) scale(1.1); }
}

@keyframes drift2 {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(-10%, -5%) scale(0.9); }
}

@keyframes drift3 {
  0% { transform: translate(0, 0) scale(1); }
  100% { transform: translate(5%, -8%) scale(1.05); }
}

/* Video styling */
.document-video {
  max-width: 100%;
  max-height: 100%;
  object-fit: contain;
}

/* Placeholders */
.image-placeholder, .video-placeholder {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.1);
  border-radius: 8px;
  padding: 1rem;
  position: relative;
}

/* Common styles */
.placeholder-icon {
  font-size: 3rem;
  margin-bottom: 0.5rem;
  opacity: 0.7;
}

.image-name, .video-name {
  font-weight: 500;
  text-align: center;
  margin-bottom: 0.5rem;
  max-width: 100%;
  word-break: break-word;
}

.image-info, .video-info {
  font-size: 0.875rem;
  color: #999;
  text-align: center;
}

.media-error-message {
  color: #ff6b6b;
  font-size: 0.75rem;
  text-align: center;
  margin-top: 0.5rem;
}

/* File icon styling */
.file-icon-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  position: relative;
  width: 100%;
  height: 100%;
}

.file-icon {
  width: 120px;
  height: 150px;
  display: flex;
  align-items: center;
  justify-content: center;
  font-weight: bold;
  font-size: 2rem;
  color: white;
  position: relative;
  margin-bottom: 16px;
}

.file-icon:before {
  content: '';
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: #e0e0e0;
  clip-path: polygon(0% 0%, 80% 0%, 100% 20%, 100% 100%, 0% 100%);
}

.file-icon:after {
  content: '';
  position: absolute;
  top: 0;
  right: 0;
  width: 20px;
  height: 20px;
  background: #c0c0c0;
  clip-path: polygon(0% 0%, 100% 0%, 100% 100%);
}

.pdf-icon:before { background: #FF5722; }
.doc-icon:before { background: #2196F3; }
.xls-icon:before { background: #4CAF50; }
.ppt-icon:before { background: #FF9800; }
.video-icon:before { background: #E91E63; }
.generic-icon:before { background: #9E9E9E; }

.document-name {
  font-size: 1rem;
  text-align: center;
  max-width: 300px;
  word-break: break-word;
}

/* Empty state */
.no-documents {
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 2rem;
  color: #999;
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}

/* Update the positions to prevent overlap */
.aspect-ratio-selector {
  position: absolute;
  bottom: 120px; /* Position higher to avoid overlap with podcast controls */
  left: 10px; /* Position on the left side */
  transform: none; /* Remove the centering transform */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
  gap: 8px;
  z-index: 5;
  opacity: 0.8;
  transition: opacity 0.3s;
  background: rgba(0, 0, 0, 0.5);
  padding: 8px 12px;
  border-radius: 8px;
}

.animation-style-selector {
  position: absolute;
  bottom: 120px; /* Same vertical position as aspect ratio selector */
  right: 10px; /* Position on the right side */
  left: auto; /* Clear any left positioning */
  transform: none; /* Remove the centering transform */
  display: flex;
  flex-direction: column;
  align-items: flex-start;
}

.aspect-ratio-selector:hover, .animation-style-selector:hover {
  opacity: 1;
}

.aspect-ratio-selector label, .animation-style-selector label {
  color: white;
  font-size: 14px;
}

.aspect-button, .animation-button {
  background: rgba(255, 255, 255, 0.1);
  border: none;
  color: white;
  padding: 4px 8px;
  font-size: 12px;
  border-radius: 4px;
  cursor: pointer;
  transition: all 0.2s;
}

.aspect-button:hover, .animation-button:hover {
  background: rgba(255, 255, 255, 0.2);
}

.aspect-button.active, .animation-button.active {
  background: rgba(66, 153, 225, 0.6);
}

.animation-buttons {
  display: flex;
  gap: 6px;
  flex-wrap: wrap;
  justify-content: center;
}

/* Fullscreen toggle button */
.fullscreen-toggle {
  position: absolute;
  top: 12px;
  right: 12px;
  width: 36px;
  height: 36px;
  border-radius: 50%;
  background: rgba(0, 0, 0, 0.6);
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  z-index: 10;
  opacity: 0.6;
  transition: opacity 0.3s, transform 0.2s;
}

.fullscreen-toggle:hover {
  opacity: 1;
  transform: scale(1.1);
}

.fullscreen-toggle.is-fullscreen {
  top: 16px;
  right: 16px;
  background: rgba(0, 0, 0, 0.8);
}

/* Responsive adjustments */
@media (max-width: 768px) {
  .stacked-document-display {
    margin-bottom: 60px; /* Space for transcript and podcast controls */
  }

  .file-icon {
    width: 80px;
    height: 100px;
    font-size: 1.5rem;
  }

  .aspect-ratio-selector, .animation-style-selector {
    padding: 6px 8px;
  }

  .aspect-button, .animation-button {
    padding: 2px 6px;
    font-size: 11px;
  }

  .animation-style-selector {
    bottom: 110px;
  }

  .fullscreen-toggle {
    width: 32px;
    height: 32px;
    top: 8px;
    right: 8px;
  }
}

/* Large play button styling */
.large-play-button-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  display: flex;
  align-items: center;
  justify-content: center;
  z-index: 4;
  cursor: pointer;
}

.large-play-button {
  width: 80px;
  height: 80px;
  border-radius: 50%;
  background: rgba(45, 29, 74, 0.8); /* Match the purple background */
  border: none;
  color: white;
  display: flex;
  align-items: center;
  justify-content: center;
  cursor: pointer;
  box-shadow: 0 4px 12px rgba(0, 0, 0, 0.3);
  transition: transform 0.2s ease, background-color 0.2s ease;
}

.large-play-button:hover {
  transform: scale(1.1);
  background: rgba(66, 153, 225, 0.9); /* Brighter blue on hover */
}

.large-play-button svg {
  margin-left: 4px; /* Slight offset to center the play icon visually */
}

.stacked-document-display.is-fullscreen {
  position: fixed;
  inset: 0; /* Shorthand for top/right/bottom/left: 0 */
  width: 100% !important;
  height: 100% !important;
  max-width: none;
  max-height: none;
  padding: 0 !important;
  margin: 0 !important;
  z-index: 9999;

  /* This helps fix the iOS Safari issue */
  top: env(safe-area-inset-top, 0);
  bottom: env(safe-area-inset-bottom, 0);
}

/* Fix for iOS Safari */
.stacked-document-display.is-fullscreen .media-container {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
}


/* Specific fixes for landscape orientation */
@media screen and (orientation: landscape) {
  .stacked-document-display.is-fullscreen {
    /* Force viewport dimensions */
    width: 100vw !important;
    height: 100vh !important;

    /* Override any margins that might be inherited */
    margin: 0 !important;

    /* Fix for iOS Safari top gap */
    top: 0 !important;
    left: 0 !important;

    /* Use viewport units to ensure full coverage */
    min-height: 100vh;

    /* Handle notches and safe areas on newer devices */
    padding: env(safe-area-inset-top, 0)
             env(safe-area-inset-right, 0)
             env(safe-area-inset-bottom, 0)
             env(safe-area-inset-left, 0) !important;
  }

  /* Ensure the content fills the container properly */
  .stacked-document-display.is-fullscreen .documents-stack,
  .stacked-document-display.is-fullscreen .media-container,
  .stacked-document-display.is-fullscreen .document-item,
  .stacked-document-display.is-fullscreen .document-container {
    position: absolute;
    inset: 0;
    height: 100% !important;
    width: 100% !important;
    margin: 0;
    padding: 0;
  }

  /* Add a black background to fill any potential gaps */
  body.has-fullscreen-element {
    background-color: #000;
    overflow: hidden; /* Prevent scrolling */
  }
}

/* Additional improvements to the fullscreen toggle method */
.stacked-document-display.is-fullscreen .fullscreen-toggle {
  /* Make exit button more visible in fullscreen mode */
  opacity: 0.8;
  background: rgba(0, 0, 0, 0.7);

  /* Position considering safe areas */
  top: max(16px, env(safe-area-inset-top, 16px));
  right: max(16px, env(safe-area-inset-right, 16px));
}
</style>
